import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import config from '../../config/env.json';

import FormWrapper from '../common/FormWrapper';
import Card from '../common/Card';
import Text from '../common/Text';
import Button from '../common/Button';
import TextInput from '../common/TextInput';
import { GoogleSignInButton, SSODivider } from '../common/SSOButtons';

import './SignIn.css';

const GOOGLE_CLIENT_ID =
  ((window as any).__GOOGLE_CLIENT_ID__ as string | undefined) ||
  (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ||
  (config as any).GOOGLE_CLIENT_ID ||
  '';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          prompt: () => void;
        };
      };
    };
  }
}

/**
 * Web SignIn — mirrors `apps/WunderGuide/views/SignIn.tsx` 1:1.
 *
 * Visual structure (RN parity):
 *   FormWrapper
 *   └── Card (maxWidth 400, alignSelf center)
 *       ├── Text variant=h2 align=center (title, marginBottom 24)
 *       ├── ssoSection (gap 12, marginBottom 8)
 *       │   ├── GoogleSignInButton
 *       │   └── SSODivider
 *       ├── TextInput placeholder=EMAIL_ADDRESS (marginBottom 16)
 *       ├── TextInput placeholder=PASSWORD secureTextEntry (marginBottom 16)
 *       └── buttonGroup (marginTop 24, gap 12)
 *           ├── Button variant=filled  title=SIGN_IN
 *           ├── Button variant=outlined title=REGISTER
 *           └── Button variant=text     title=FORGOT_PASSWORD
 *
 * Apple SSO is iOS-only on RN — never rendered on web.
 */
const SignIn: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { login, loginWithGoogle, authError } = useAuth();

  const [emailAddress, setEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isProcessingRef = useRef(false);
  const googleInitializedRef = useRef(false);

  const isSSOEnabled = !!GOOGLE_CLIENT_ID;
  const anyLoading = isLoading || isGoogleLoading;

  // Surface auth errors from the context
  useEffect(() => {
    if (authError) {
      setError(authError);
      setIsLoading(false);
      isProcessingRef.current = false;
    }
  }, [authError]);

  // Initialize Google Identity Services for the custom button. We use our own
  // styled button (matching RN) and trigger Google's prompt on click.
  const handleGoogleResponse = useCallback(
    async (response: { credential: string }) => {
      setError(null);
      try {
        await loginWithGoogle(response.credential);
        navigate('/');
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Google sign-in failed');
      } finally {
        setIsGoogleLoading(false);
        isProcessingRef.current = false;
      }
    },
    [loginWithGoogle, navigate],
  );

  useEffect(() => {
    if (!isSSOEnabled || googleInitializedRef.current) return;
    const init = () => {
      if (!window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
      });
      googleInitializedRef.current = true;
    };
    if (window.google?.accounts?.id) {
      init();
      return;
    }
    const interval = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(interval);
        init();
      }
    }, 100);
    return () => clearInterval(interval);
  }, [isSSOEnabled, handleGoogleResponse]);

  const handleLogin = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    if (isLoading || isProcessingRef.current) return;
    try {
      isProcessingRef.current = true;
      setIsLoading(true);
      setError(null);
      // CRITICAL: iOS Safari autofill writes values into the DOM via a
      // direct setter that does NOT dispatch React's synthetic onChange —
      // so controlled state (`emailAddress`, `password`) stays empty
      // even though the user sees their filled credentials. Always read
      // straight off the form on submit so autofilled values actually go
      // out. Fall back to React state if the call wasn't via form submit
      // (e.g. legacy click path before the <form> wrapper landed).
      const form = e?.currentTarget;
      const fd = form ? new FormData(form) : null;
      const rawEmail = (fd?.get('email') as string | null) ?? emailAddress;
      const rawPass = (fd?.get('password') as string | null) ?? password;
      const normalisedEmail = rawEmail.trim().toLowerCase();
      await login(normalisedEmail, rawPass);
      navigate('/');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
      isProcessingRef.current = false;
    }
  };

  const handleGoogleSignIn = () => {
    if (isGoogleLoading || isProcessingRef.current) return;
    if (!window.google?.accounts?.id) return;
    isProcessingRef.current = true;
    setIsGoogleLoading(true);
    setError(null);
    window.google.accounts.id.prompt();
  };

  return (
    <FormWrapper>
      <Card className="signin-card">
        <Text variant="h2" align="center" className="signin-title">
          {t('SIGN_IN') || 'Sign in'}
        </Text>

        {isSSOEnabled && (
          <div className="signin-sso-section">
            <GoogleSignInButton
              onClick={handleGoogleSignIn}
              loading={isGoogleLoading}
              disabled={anyLoading}
            />
            <SSODivider />
          </div>
        )}

        {/* Real <form> wrapper so iOS Safari recognises this as a login
            form and autofill works correctly. `name` attributes + the
            standard autocomplete tokens are what Password Manager keys on. */}
        <form className="signin-form" onSubmit={handleLogin} autoComplete="on">
          <TextInput
            className="signin-input"
            inputStyle={{ paddingTop: 16, paddingBottom: 16 }}
            borderRadius={20}
            placeholder={t('EMAIL_ADDRESS') || 'Email address'}
            value={emailAddress}
            onChangeText={setEmailAddress}
            type="email"
            inputMode="email"
            name="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="username"
            showClearButton={false}
          />

          <TextInput
            className="signin-input"
            inputStyle={{ paddingTop: 16, paddingBottom: 16 }}
            borderRadius={20}
            placeholder={t('PASSWORD') || 'Password'}
            value={password}
            onChangeText={setPassword}
            name="password"
            secureTextEntry
            autoComplete="current-password"
          />

          {error && (
            <div className="signin-error">
              <Text variant="body">{error}</Text>
            </div>
          )}

          <div className="signin-button-group">
            <Button
              title={t('SIGN_IN') || 'Sign in'}
              variant="filled"
              type="submit"
              loading={isLoading}
              disabled={anyLoading}
            />
            <Button
              title={t('REGISTER') || 'Register'}
              variant="outlined"
              type="button"
              onClick={() => navigate('/register')}
              disabled={anyLoading}
            />
            <Button
              title={t('FORGOT_PASSWORD') || 'Forgot password?'}
              variant="text"
              type="button"
              onClick={() => navigate('/forgot-password')}
              disabled={anyLoading}
            />
          </div>
        </form>
      </Card>
    </FormWrapper>
  );
};

export default SignIn;
